import styled from "@emotion/styled";
import DateTimeRangePicker from "components/common/DateTimeRangePicker";
import TimezoneDropdown from "components/common/TimezoneDropdown";
import { DownloadOption } from "components/gateways/DownloadOption";
import { useOrganisation } from "hooks/organization";
import { lowerCase, map, union, uniqBy, upperFirst } from "lodash";
import moment, { Moment } from "moment";
import { ChangeEvent, memo, useEffect, useState } from "react";
import { Button, Card, Form, Modal, Row } from "react-bootstrap";
import styledConst from "styles";
import { Device, DownloadOptions, DownloadPayload, WidgetSource } from "types";
import { ConfirmDownloadModal } from "./ConfirmDownloadModal";
import { ChartPanel } from "types/Charts";
import { ChartSource, ChartType } from "constant/chart";
import { isPSLWidget } from "utils/chart";

interface Props {
    show: boolean;
    panel: ChartPanel;
    deviceConfig?: any;
    onClose?: () => void;
}

type DeviceDownload = Device & {
    id: string;
    port: string;
};

const DownloadModal = styled(Modal)`
    .modal-dialog {
        max-width: 635px;
    }
`;

const Box = styled(Card)`
    border: 2px solid #eaeaea;
`;

const DeviceItem = styled(Card)`
    margin-bottom: 1rem;
    border: 2px solid #eaeaea;

    .card-body {
        display: flex;
        justify-content: space-between;
    }
`;

const DeviceInfo = styled.div`
    color: ${styledConst.Primary_Blue_2};

    .subtext {
        font-size: 0.75rem;
        color: #828fab;
        opacity: 0.75;
    }
`;

const DeviceControl = styled.div`
    align-self: center;
`;

const CheckBox = styled(Form.Check)`
    .custom-control-label {
        color: ${styledConst.Primary_Blue_3};

        &::before {
            background-color: #dddddd;
            border-color: #dddddd;
        }
    }
`;

const isHeartbeatOnly = (panel: ChartPanel) => {
    const { chart, source } = panel;
    return (source === ChartSource.SINGLE_GATEWAY && chart === ChartType.DONUT) || // Gateway Status Statistic
        (source === ChartSource.MULTIPLE_GATEWAYS && chart.startsWith("CARD")); // Table -List of Gateways, Card - Gateway Uptime History
}

const DownloadChartModalComponent = (props: Props) => {
    const { defaultTimeZone } = useOrganisation();
    const [showDeviceModal, setShowDeviceModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [startDateTime, setStartDateTime] = useState(
        moment.utc().subtract(24, "hours").tz(defaultTimeZone)
    );
    const [endDateTime, setEndDateTime] = useState(
        moment().tz(defaultTimeZone)
    );
    const [timezone, setTimezone] = useState<string>(defaultTimeZone);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [devices, setDevices] = useState<DeviceDownload[]>([]);
    const [errMsg, setErrMsg] = useState("");
    const { show, panel, onClose, deviceConfig } = props;
    const [downloadOption, setDownloadOption] =
        useState<DownloadOptions>("email");
    const [isDisableFastDownload, setIsDisableFastDownload] = useState(false);
    const [widgetSource, setWidgetSource] = useState(WidgetSource.Sensor);

    useEffect(() => {
        if (show) {
            setShowDeviceModal(show);
            setDownloadOption("email");
            setIsDisableFastDownload(false);
            setStartDateTime(
                moment.utc().subtract(24, "hours").tz(defaultTimeZone)
            );
            setEndDateTime(moment().tz(defaultTimeZone));
            setTimezone(defaultTimeZone);
            setSelectedIds([]);
            setErrMsg("");
        }
    }, [show, defaultTimeZone]);

    useEffect(() => {
        setTimezone(defaultTimeZone);
    }, [defaultTimeZone]);

    useEffect(
        () => {
            if (panel && showDeviceModal) {
                getDevicesInfo();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [panel, showDeviceModal]
    );

    const getSourceName = () => {

        if (widgetSource === WidgetSource.PSL) {
            return getPSLSourceName();
        }

        const deviceName = lowerCase(panel?.source || "");

        if (deviceName === "sensors and actuators") {
            return "Sensor(s) and Actuator(s)";
        }

        let result = "";
        result += deviceName.includes("sensor") ? "Sensor" : "";
        result += deviceName.includes("actuator") ? "Actuator" : "";
        result += deviceName.includes("gateway") || deviceName === "storage" ? "Gateway" : "";
        result += deviceName.includes("ldsu") ? "LDSU" : "";

        if (deviceName.includes("multiple")) {
            result += "(s)";
        }
        return result;
    };

    const getPSLSourceName = () => {
        const { chart, attributes: [attr1] } = panel;
        if (chart === ChartType.CARD_STATUS) {
            return "Rooms/Zones/Groups";
        }

        return upperFirst(attr1?.name ?? "Room");
    }

    const getDevicesInfo = async () => {
        const { source, chart } = panel;
        const devices = (deviceConfig ?? []).map((device: any) => ({
            ...device,
            id: `${device.gatewayId}_${device.deviceId ?? ""}_${device.said ?? ""}`,
        }));

        setDevices(devices);
        setSelectedIds(
            map(devices, (device: any) => device.id)
        );

        if (source.includes("GATEWAY") || source === "STORAGE") {
            setWidgetSource(WidgetSource.Gateway);
        } else if (source.includes("LDSU")) {
            setWidgetSource(WidgetSource.LDSU);
            setDevices(uniqBy(devices, (device: any) => `${device.deviceId}_${device.gatewayId}`));
        } else if (isPSLWidget(source, chart)) {
            setWidgetSource(WidgetSource.PSL);
        }
    };

    const isDisabledDownload = () => {
        return selectedIds.length === 0 || errMsg !== "";
    };

    const handleDownloadClick = () => {
        setShowDeviceModal(false);
        setShowConfirmModal(true);
    };

    const handleConfirmClick = async (
        processConfirm: (data: DownloadPayload) => Promise<void> | void
    ) => {
        // Validate that dates are not empty
        if (
            !startDateTime ||
            !endDateTime ||
            !startDateTime.isValid() ||
            !endDateTime.isValid()
        ) {
            setErrMsg("Date and time are required");
            return;
        }

        const _devices = devices
            .filter((item) => selectedIds.includes(item.id))
            .map(({ gatewayId: gateway_id, deviceId: device_id, said }) => ({
                gateway_id,
                heartbeat_only: isHeartbeatOnly(panel),
                ...(widgetSource !== WidgetSource.Gateway
                    ? { device_id: device_id }
                    : {}),
                ...([WidgetSource.Sensor, WidgetSource.PSL].includes(widgetSource)
                    ? { said: Number(said) }
                    : {}),
            }));
        const params = {
            start: moment(startDateTime).seconds(0).unix(),
            end: moment(endDateTime).seconds(0).unix(),
            time_zone: timezone,
            attributes: _devices,
        };

        await processConfirm(params);
    };

    const handleCloseModal = () => {
        setShowDeviceModal(false);
        setShowConfirmModal(false);
        setSelectedIds(map(devices, "id"));
        onClose?.();
    };

    const handleSelectDevice = (
        e: ChangeEvent<HTMLInputElement>,
        device: DeviceDownload | string
    ) => {
        const { checked } = e.target;
        let ids = selectedIds;
        const _device = device as DeviceDownload;

        if (checked) {
            if (device === "all") {
                ids = map(devices, "id");
            } else {
                ids = union(selectedIds, [_device.id]);
            }
        } else if (device === "all") {
            ids = [];
        } else {
            ids = ids.filter((id: string) => id !== _device.id);
        }

        setSelectedIds(ids);
    };

    const handleChangeTimeZone = (timezone: string) => {
        setTimezone(timezone);
    };

    const getDisplayGatewayName = ({
        port,
        gatewayName,
    }: {
        port: string;
        gatewayName: string;
    }) => {
        return port ? `${gatewayName} - LDSBUS ${port}` : `${gatewayName}`;
    };

    const handleDateTimeRangeChange = (value: Moment[]) => {
        setStartDateTime(value[0]);
        setEndDateTime(value[1]);
        const disabledFastDownload =
            value[1].diff(value[0], "minute") > 24 * 60;
        setIsDisableFastDownload(disabledFastDownload);

        if (disabledFastDownload) {
            setDownloadOption("email");
        }
    };

    const handleDateTimeRangeError = (error: string) => {
        setErrMsg(error);
    };

    const onChangeDownloadOption = (option: DownloadOptions) => {
        setDownloadOption(option);
    };

    const renderDeviceList = () => {
        return devices.map((device, index) => (
            <DeviceItem body key={device.id}>
                <DeviceInfo>
                    <Row className="device-name">
                        <span
                            className={`d-flex ${device.name
                                ? "mute"
                                : "text-secondary-red-1"
                                }`}
                        >
                            {!device.name && (
                                <span className="material-icons mr-2">
                                    error_outline
                                </span>
                            )}
                            <span aria-label="device_display_name">
                                {device.sensorName ??
                                    "Device information not found"}
                            </span>
                        </span>
                    </Row>
                    <Row className="subtext device-ldsu">
                        {getDisplayGatewayName({
                            port: device.port,
                            gatewayName: device.gatewayName,
                        })}
                    </Row>
                    <Row className="subtext device-id">
                        LDSU UUID: {device.deviceId}
                    </Row>
                    <Row className="subtext sensor-name">
                        {device.sensorType}
                    </Row>
                </DeviceInfo>
                <DeviceControl>
                    {devices.length > 1 && (
                        <CheckBox
                            custom
                            type="checkbox"
                            id={`device-${index}`}
                            checked={selectedIds.includes(
                                device.id
                            )}
                            onChange={(e) =>
                                handleSelectDevice(
                                    e,
                                    device
                                )
                            }
                        />
                    )}
                </DeviceControl>
            </DeviceItem>
        ));
    };

    const renderLDUSList = () => {
        return devices.map((device) => (
            <DeviceItem body key={device.id}>
                <DeviceInfo>
                    <Row className="device-name">
                        <span
                            className={`d-flex ${device.name
                                ? "mute"
                                : "text-secondary-red-1"
                                }`}
                        >
                            {!device.name && (
                                <span className="material-icons mr-2">
                                    error_outline
                                </span>
                            )}
                            <span aria-label="device_display_name">
                                {device.name ??
                                    "Device information not found"}
                            </span>
                        </span>
                    </Row>
                    <Row className="subtext device-id">
                        UUID: {device.deviceId}
                    </Row>
                </DeviceInfo>
            </DeviceItem>
        ));
    };

    const renderGatewayList = () => {
        return devices.map((device, index) => (
            <DeviceItem body key={device.id}>
                <DeviceInfo>
                    <Row className="device-name">
                        <span
                            className={`d-flex ${device.gatewayName
                                ? "mute"
                                : "text-secondary-red-1"
                                }`}
                        >
                            {!device.gatewayName && (
                                <span className="material-icons mr-2">
                                    error_outline
                                </span>
                            )}
                            <span aria-label="device_display_name">
                                {device.gatewayName ??
                                    "Device information not found"}
                            </span>
                        </span>
                    </Row>
                    <Row className="subtext device-id">
                        UUID: {device.gatewayId}
                    </Row>
                </DeviceInfo>
                <DeviceControl>
                    {devices.length > 1 && (
                        <CheckBox
                            custom
                            type="checkbox"
                            id={`device-${index}`}
                            checked={selectedIds.includes(
                                device.id
                            )}
                            onChange={(e) =>
                                handleSelectDevice(
                                    e,
                                    device
                                )
                            }
                        />
                    )}
                </DeviceControl>
            </DeviceItem>
        ));
    };

    const renderRZRList = () => {
        return devices.map((device, index) => (
            <DeviceItem body key={device.id}>
                <DeviceInfo>
                    <Row className="device-name">
                        <span
                            className={`d-flex ${device.deviceName
                                ? "mute"
                                : "text-secondary-red-1"
                                }`}
                        >
                            {!device.deviceName && (
                                <span className="material-icons mr-2">
                                    error_outline
                                </span>
                            )}
                            <span aria-label="device_display_name">
                                {device.deviceName ??
                                    "Device information not found"}
                            </span>
                        </span>
                    </Row>
                    <Row className="subtext device-ldsu">
                        {device.gatewayName}
                    </Row>
                    <Row className="subtext device-id">
                        Network ID: {device.deviceId}
                    </Row>
                    <Row className="subtext sensor-name">
                        {upperFirst(device.deviceType)}
                    </Row>
                </DeviceInfo>
                <DeviceControl>
                    {devices.length > 1 && (
                        <CheckBox
                            custom
                            type="checkbox"
                            id={`device-${index}`}
                            checked={selectedIds.includes(
                                device.id
                            )}
                            onChange={(e) =>
                                handleSelectDevice(
                                    e,
                                    device
                                )
                            }
                        />
                    )}
                </DeviceControl>
            </DeviceItem>
        ));
    };

    const renderMap: Partial<Record<WidgetSource, () => JSX.Element[]>> = {
        [WidgetSource.Gateway]: renderGatewayList,
        [WidgetSource.LDSU]: renderLDUSList,
        [WidgetSource.PSL]: renderRZRList,
        [WidgetSource.Sensor]: renderDeviceList,
        [WidgetSource.Actuator]: renderDeviceList,
    };

    return (
        <>
            <DownloadModal
                className=""
                show={showDeviceModal}
                onHide={handleCloseModal}
                aria-labelledby="contained-modal-title-vcenter"
                centered
                size="lg"
                scrollable
            >
                <Modal.Header>
                    <Modal.Title id="example-modal-sizes-title-sm">
                        Download Data
                    </Modal.Title>
                    <Button
                        variant=""
                        className="close-button"
                        onClick={handleCloseModal}
                    >
                        <span className="material-icons">close</span>
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <div>
                            <Form.Label className="text-primary-blue-3 mb-3">
                                Time Zone
                            </Form.Label>
                            <Box body>
                                <Form.Label className="text-primary-blue-3">
                                    Select a time zone
                                </Form.Label>
                                <TimezoneDropdown
                                    value={timezone}
                                    onChange={handleChangeTimeZone}
                                />
                            </Box>
                        </div>
                        <div className="mt-3">
                            <Form.Label className="text-primary-blue-3 mb-3">
                                Date/time Range
                            </Form.Label>
                            <Box body>
                                <DateTimeRangePicker
                                    inline={true}
                                    value={[startDateTime, endDateTime]}
                                    timezone={timezone}
                                    onChange={handleDateTimeRangeChange}
                                    onError={handleDateTimeRangeError}
                                />
                            </Box>
                        </div>

                        <div className="mt-4">
                            <div className="d-flex justify-content-between">
                                <Form.Label className="text-primary-blue-3 mb-3">
                                    {getSourceName()}
                                </Form.Label>
                                {devices.length > 1 && (
                                    <CheckBox
                                        custom
                                        className="pr-1"
                                        type="checkbox"
                                        id="device-all"
                                        label="All"
                                        checked={
                                            selectedIds.length > 0 &&
                                            selectedIds.length ===
                                            devices.length
                                        }
                                        onChange={(e) =>
                                            handleSelectDevice(e, "all")
                                        }
                                    />
                                )}
                            </div>
                            {(renderMap[widgetSource] ?? renderDeviceList)()}
                        </div>
                        <div className="mt-3">
                            <DownloadOption
                                selectedOption={downloadOption}
                                onChange={onChangeDownloadOption}
                                fastDisabled={isDisableFastDownload}
                            />
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="btn-create float-right"
                        disabled={isDisabledDownload()}
                        onClick={handleDownloadClick}
                    >
                        Download
                    </Button>
                </Modal.Footer>
            </DownloadModal>
            <ConfirmDownloadModal
                show={showConfirmModal}
                onClose={handleCloseModal}
                downloadType={downloadOption}
                onConfirm={handleConfirmClick}
            />
        </>
    );
};

export const DownloadChartModal = memo(DownloadChartModalComponent);
